import { useState, useContext, useEffect, useCallback } from 'react';
import './style.css';
import { devicecontext } from '../../../context/device/context';

/**
 * Normaliza un string para usarlo como valor de hash en la URL.
 * Ej: "Mis Productos" → "mis-productos"
 */
const slugify = (str) =>
  String(str)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '');

/**
 * Lee el valor de una clave desde window.location.hash.
 * Ej: hash="#tab=servicios" → getHashParam("tab") → "servicios"
 */
const getHashParam = (key) => {
  const raw = window.location.hash.replace(/^#/, '');
  const params = new URLSearchParams(raw);
  return params.get(key);
};

/**
 * Escribe un parámetro en el hash de la URL sin recargar la página.
 * Preserva otros parámetros de hash existentes.
 */
const setHashParam = (key, value) => {
  const raw = window.location.hash.replace(/^#/, '');
  const params = new URLSearchParams(raw);
  params.set(key, value);
  window.location.hash = params.toString();
};

const TabSwitcher = ({
  tabs,
  onTabChange,
  activeTabIndex = 0,
  controlledIndex,
  tabStyle = {},
  /** Clave del hash de la URL para persistir el tab activo. Ej: "tab" → #tab=productos */
  hashKey,
}) => {
  const { isMobileDevice } = useContext(devicecontext) || {};
  const isMobile = typeof isMobileDevice === 'function' ? isMobileDevice() : false;

  /**
   * Determina el índice inicial leyendo el hash de la URL (si hashKey está definido).
   * Si no hay coincidencia en la URL, usa activeTabIndex como fallback.
   */
  const getInitialIndex = useCallback(() => {
    if (!hashKey || !tabs?.length) return activeTabIndex;
    const hashValue = getHashParam(hashKey);
    if (!hashValue) return activeTabIndex;
    const found = tabs.findIndex((t) => slugify(t.name) === hashValue);
    return found !== -1 ? found : activeTabIndex;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [activeTab, setActiveTab] = useState(getInitialIndex);

  // Escribe el hash inicial en la URL al montar (si hashKey está definido y no había hash)
  useEffect(() => {
    if (!hashKey || !tabs?.length) return;
    const current = getHashParam(hashKey);
    const currentSlug = slugify(tabs[activeTab]?.name ?? '');
    if (!current) {
      setHashParam(hashKey, currentSlug);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sincroniza con controlledIndex (control externo)
  useEffect(() => {
    if (controlledIndex !== undefined) {
      setActiveTab(controlledIndex);
    }
  }, [controlledIndex]);

  // Sincroniza con activeTabIndex (prop externa)
  useEffect(() => {
    if (activeTabIndex !== undefined && activeTabIndex !== activeTab) {
      setActiveTab(activeTabIndex);
    }
  }, [activeTabIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escucha el evento hashchange (botón Atrás/Adelante del navegador)
  useEffect(() => {
    if (!hashKey || !tabs?.length) return;

    const onHashChange = () => {
      const hashValue = getHashParam(hashKey);
      if (!hashValue) return;
      const found = tabs.findIndex((t) => slugify(t.name) === hashValue);
      if (found !== -1 && found !== activeTab) {
        setActiveTab(found);
        if (onTabChange) onTabChange(found);
      }
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [hashKey, tabs, activeTab, onTabChange]);

  const activateTab = useCallback(
    (index) => {
      setActiveTab(index);
      if (onTabChange) onTabChange(index);
      if (hashKey && tabs?.[index]) {
        setHashParam(hashKey, slugify(tabs[index].name));
      }
    },
    [hashKey, tabs, onTabChange]
  );

  const handleTabChange = (event) => {
    activateTab(parseInt(event.target.value, 10));
  };

  const handleTabClick = (index) => {
    activateTab(index);
  };

  return (
    <div className="tab-switcher-container">
      {isMobile ? (
        <select className="tab-dropdown" onChange={handleTabChange} value={activeTab}>
          {tabs.map((tab, index) => (
            <option key={index} value={index}>
              {tab.name}
              {tab.badge != null && Number(tab.badge) > 0 ? ` (${tab.badge})` : ''}
            </option>
          ))}
        </select>
      ) : (
        <div className="tab-headers" style={tabStyle}>
          {tabs.map((tab, index) => (
            <button
              key={index}
              type="button"
              className={`tab-header ${index === activeTab ? 'active' : ''}`}
              onClick={() => handleTabClick(index)}
              disabled={tab.disabled ? tab.disabled : false}
            >
              <span className="tab-header-title">{tab.name}</span>
              {tab.badge != null && Number(tab.badge) > 0 && (
                <span
                  className={`tab-header-badge ${
                    tab.badgeVariant ? `badge-${tab.badgeVariant}` : 'badge-danger'
                  }`}
                >
                  {Number(tab.badge) > 99 ? '99+' : tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      <div className="tab-content">
        {tabs[activeTab] && (
          typeof tabs[activeTab].component === 'function'
            ? tabs[activeTab].component()
            : tabs[activeTab].component
        )}
      </div>
    </div>
  );
};

export default TabSwitcher;
